import { Locale } from '@tuic/i18n';
import { fireImmediateTelemetry } from './telemetry';
import PrismAPI from '@modules/prism-client/types/prism-types';

/**
 * Invoke a PRISM action with type-safe request/response handling
 *
 * Automatically fires immediate telemetry from error responses.
 * If the action fails and includes telemetry instructions, they will be
 * executed immediately (e.g., form-error tracking).
 *
 * @template T - Action type extending Action<any, any>
 * @param action - Action definition from PRISM API
 * @param payload - Request payload (optional)
 * @returns Promise resolving to typed action response
 *
 * @example
 * ```ts
 * const result = await invokePrismAction<AddBookingAction>(
 *   dialog.form.action,
 *   { firstName, lastName, bookingCode }
 * );
 *
 * if (result.success) {
 *   // result.data is typed
 * } else {
 *   // result.error.telemetry already fired
 * }
 * ```
 */
export const invokePrismAction = async <
  Action extends PrismAPI.Action<any, any>,
>(
  action: PrismAPI.ActionDefinition,
  payload: PrismAPI.PayloadFromAction<Action>,
  locale?: Locale | string,
): Promise<PrismAPI.ResponseFromAction<Action>> => {
  const actionUrl = new URL(action.endpoint);
  if (locale) {
    actionUrl.searchParams.append('locale', locale);
  }

  const response = await fetch(actionUrl.toString(), {
    method: action.method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: action.token, payload }),
  });

  const result: PrismAPI.ResponseFromAction<Action> = await response.json();

  // Fire immediate telemetry from error responses
  if (!result.success && result.error) {
    fireImmediateTelemetry(result.error);
  }

  return result;
};
